/**
 * Per-diem suppression helpers for the covered charges table.
 *
 * A per-diem program (HealthcareService with isPerDiem === true) declares the
 * set of procedure codes that get rolled up into a per-diem charge. Those
 * procedures should not appear on the covered charges list while the encounter
 * is in-progress — they are routed exclusively to the per-diem report instead.
 *
 * These helpers are pure (no Vue, no API) so they can be unit-tested and
 * reused outside the composable layer.
 */

const PART_OF_WALK_MAX_DEPTH = 10;

/**
 * Walk an encounter's `partOf` chain (via `encounterById`) up to the enrollment encounter whose
 * `healthcareService.id` is one of `programIds`, and return that id. This is the canonical way to
 * find the program a clinical encounter belongs to: the program is selected when the appointment
 * is started and persisted onto the enrollment encounter at the top of the chain — never on the
 * form encounter or procedure itself.
 *
 * @param {object} encounter        the encounter to start from (e.g. a form encounter)
 * @param {Map<string, object>} encounterById  every encounter in the chain, indexed by id
 * @param {Set<string>} programIds   ids that count as a match (e.g. the visible per-diem programs)
 * @returns {string|null} the matched program id, or null if none is reached within the depth cap
 */
export function resolveEncounterProgramId(encounter, encounterById, programIds) {
    if (!(programIds instanceof Set) || programIds.size === 0) return null;

    let current = encounter;
    const seen = new Set();
    for (let depth = 0; depth < PART_OF_WALK_MAX_DEPTH; depth++) {
        if (!current?.id || seen.has(current.id)) return null;
        seen.add(current.id);

        const hsId = current.healthcareService?.id;
        if (hsId && programIds.has(hsId)) return hsId;

        const parentId = current.partOf?.id;
        if (!parentId) return null;
        current = encounterById instanceof Map ? encounterById.get(parentId) : undefined;
    }
    return null;
}

/**
 * Resolve the set of per-diem applicable procedure codes for a single program.
 *
 * Gates on `isPerDiem === true` — a non-per-diem program (even one carrying a stray
 * `perDiemProcedure` reference) resolves to an empty Set, so its session codes are never
 * suppressed (#4728). Codes come from either (a) the program's own
 * `perDiem.applicableServices`, or (b) the referenced billable HealthcareService
 * (`program.perDiemProcedure.id`, resolved through `hsById`). In current BN data the
 * convention is (b); (a) is a fallback for data that diverges.
 *
 * Shared by both per-diem suppression surfaces: the covered-charges table
 * (`buildPerDiemSuppressionMap`) and the sign-and-lock charge-creation gate
 * (`useQuestionnaireFlow.findPerDiemApplicableCodesFromEncounters`).
 *
 * @param {object} program  HealthcareService program record
 * @param {Map<string, object>} hsById  HealthcareService records indexed by id
 * @returns {Set<string>} applicable procedure codes (empty when the program is not per-diem)
 */
export function resolvePerDiemApplicableCodes(program, hsById) {
    const codes = new Set();
    if (!program || program.isPerDiem !== true) return codes;

    const direct = program.perDiem?.applicableServices;
    if (Array.isArray(direct)) {
        for (const as of direct) if (as?.code) codes.add(as.code);
    }

    const billableId = program.perDiemProcedure?.id;
    if (billableId && hsById instanceof Map) {
        const referenced = hsById.get(billableId)?.perDiem?.applicableServices;
        if (Array.isArray(referenced)) {
            for (const as of referenced) if (as?.code) codes.add(as.code);
        }
    }

    return codes;
}

/**
 * The CROSS-PROGRAM set of per-diem service codes plus the ids of the programs that contributed
 * them. The union of every per-diem program's applicable codes (`resolvePerDiemApplicableCodes`,
 * which also follows the delegated `perDiemProcedure` reference through `hsById`). A service is
 * per-diem when it enrolls under a per-diem program AND its code is a per-diem code for ANY program
 * — codes are intentionally cross-cut from the program (an appointment under one program can carry
 * another program's per-diem code). This is the SINGLE definition shared by the per-diem report
 * (shows a service when its code is in `codes`) and `buildPerDiemSuppressionMap` (suppresses the
 * same set from covered charges), so the two reports partition cleanly and can't drift.
 *
 * @param {Array<object>} programs  HealthcareService records to scan (non-per-diem ones contribute nothing)
 * @param {Map<string, object>} hsById  HealthcareService records indexed by id (for perDiemProcedure resolution)
 * @returns {{ codes: Set<string>, programIds: Set<string> }}
 */
export function collectPerDiemCodes(programs, hsById) {
    const codes = new Set();
    const programIds = new Set();
    if (!Array.isArray(programs)) return { codes, programIds };

    for (const program of programs) {
        if (!program?.id) continue;
        const programCodes = resolvePerDiemApplicableCodes(program, hsById);
        if (programCodes.size === 0) continue;
        programIds.add(program.id);
        for (const code of programCodes) codes.add(code);
    }
    return { codes, programIds };
}

/**
 * Build an encounter → applicable-codes suppression map.
 *
 * @param {Array<object>} programs   HealthcareService records (filtered internally to isPerDiem)
 * @param {Array<object>} encounters Encounter records with id, optional partOf.id, optional healthcareService.id
 * @returns {Map<string, Set<string>>} keyed by Encounter.id whose partOf chain resolves to a per-diem program
 */
export function buildPerDiemSuppressionMap(programs, encounters) {
    const result = new Map();
    if (!Array.isArray(programs) || !Array.isArray(encounters)) return result;

    // Index every HealthcareService by id so we can resolve perDiemProcedure references.
    // The `programs` argument is the flat fetch result, which includes both program-level
    // records (isPerDiem: true) and the per-diem billable services they reference via
    // perDiemProcedure (pulled in by _include=HealthcareService:perDiemProcedure).
    const hsById = new Map();
    for (const p of programs) {
        if (p?.id) hsById.set(p.id, p);
    }

    const { codes: perDiemCodes, programIds } = collectPerDiemCodes(programs, hsById);
    if (!perDiemCodes.size) return result;

    // Index encounters by id for partOf walking.
    const encounterById = new Map();
    for (const enc of encounters) {
        if (enc?.id) encounterById.set(enc.id, enc);
    }

    // Every encounter enrolled under a per-diem program maps to the same cross-program code set;
    // isSuppressedByPerDiem then hides a row whose code is in it.
    for (const encounter of encounters) {
        if (!encounter?.id) continue;
        if (!resolveEncounterProgramId(encounter, encounterById, programIds)) continue;
        result.set(encounter.id, perDiemCodes);
    }

    return result;
}

/**
 * Decide whether a covered-charges row should be suppressed (hidden) because
 * its procedures belong to a per-diem program.
 *
 * @param {object} row
 * @param {string} row.encounterId
 * @param {string|string[]} row.procedureCodes  Either '-' sentinel, a single code, or an array of codes
 * @param {string|null|undefined} row.chargeItemStatus  'planned' | 'billed' | 'not-billable' | null
 * @param {Map<string, Set<string>>} suppressionMap
 * @returns {boolean} true → hide row from covered charges
 */
export function isSuppressedByPerDiem({ encounterId, procedureCodes, chargeItemStatus }, suppressionMap) {
    // Settled rows (already bundled or explicitly bypassed) stay visible.
    if (chargeItemStatus === 'billed' || chargeItemStatus === 'not-billable') return false;
    if (!(suppressionMap instanceof Map)) return false;
    if (!encounterId) return false;

    const codes = suppressionMap.get(encounterId);
    if (!codes || !codes.size) return false;

    // `procedureCodes` may be a single string or the '-' sentinel that
    // transformSqlRowsToItems uses for rows with no procedures. Both are safely
    // handled: '-' is never an applicable code, so it can't match.
    const codeList = Array.isArray(procedureCodes) ? procedureCodes : [procedureCodes];
    for (const code of codeList) {
        if (code && codes.has(code)) return true;
    }
    return false;
}
